import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Ear, Menu, X, User, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/partenaires", label: "Partenaires" },
  { to: "/centres", label: "Nos centres" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function dashboardPath(role) {
  if (role === "admin" || role === "superadmin") return "/administration";
  if (role === "center") return "/espace-centre";
  return "/espace-client";
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-11 h-11 rounded-full bg-primary-blue text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <Ear size={22} strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-xl font-semibold text-primary-blue">Audition Expert</div>
              <div className="text-xs uppercase tracking-widest text-gold">Conseil · Rendez-vous gratuit</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={`px-4 py-2 rounded-md text-[15px] font-medium transition-colors ${
                    active ? "text-primary-blue bg-sky-blue" : "text-slate-700 hover:text-primary-blue hover:bg-sky-blue/60"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Link to={dashboardPath(user.role)} data-testid="nav-dashboard">
                  <Button variant="outline" className="border-primary-blue text-primary-blue hover:bg-sky-blue">
                    <LayoutDashboard size={18} className="mr-2" /> Mon espace
                  </Button>
                </Link>
                <Button onClick={handleLogout} variant="ghost" className="text-slate-700" data-testid="nav-logout">
                  <LogOut size={18} className="mr-2" /> Déconnexion
                </Button>
              </>
            ) : (
              <>
                <Link to="/connexion" data-testid="nav-login">
                  <Button variant="ghost" className="text-primary-blue">
                    <User size={18} className="mr-2" /> Connexion
                  </Button>
                </Link>
                <Link to="/prendre-rendez-vous" data-testid="nav-cta-appointment">
                  <Button className="bg-primary-blue hover:bg-[#0a1d36] text-white font-semibold shadow-md">
                    Rendez-vous <span className="ml-2 text-gold font-bold">GRATUIT</span>
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2 text-primary-blue"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-2">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="py-3 px-3 rounded-md text-lg text-slate-700 hover:bg-sky-blue"
                  data-testid={`mnav-${n.to.replace(/\//g, "") || "home"}`}
                >
                  {n.label}
                </Link>
              ))}
              <div className="pt-2 border-t border-slate-200 mt-2 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to={dashboardPath(user.role)} onClick={() => setOpen(false)}>
                      <Button className="w-full bg-primary-blue text-white">Mon espace</Button>
                    </Link>
                    <Button onClick={handleLogout} variant="outline" className="w-full">Déconnexion</Button>
                  </>
                ) : (
                  <>
                    <Link to="/connexion" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full">Connexion</Button>
                    </Link>
                    <Link to="/prendre-rendez-vous" onClick={() => setOpen(false)}>
                      <Button className="w-full bg-primary-blue text-white">
                        Rendez-vous <span className="ml-2 text-gold font-bold">GRATUIT</span>
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-primary-blue text-white mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white text-primary-blue flex items-center justify-center">
                <Ear size={20} />
              </div>
              <div className="font-heading text-xl">Audition Expert</div>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">
              Service gratuit de mise en relation avec des centres auditifs partenaires en France.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-gold text-sm">
              <ShieldCheck size={16} /> Prise de rendez-vous gratuite
            </div>
          </div>
          <div>
            <div className="font-heading text-lg mb-4">Navigation</div>
            <ul className="space-y-2 text-white/80 text-sm">
              <li><Link to="/comment-ca-marche" className="hover:text-white">Comment ça marche</Link></li>
              <li><Link to="/partenaires" className="hover:text-white">Partenaires</Link></li>
              <li><Link to="/centres" className="hover:text-white">Nos centres</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading text-lg mb-4">Informations</div>
            <ul className="space-y-2 text-white/80 text-sm">
              <li><Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
              <li><Link to="/cgu" className="hover:text-white">Conditions générales</Link></li>
              <li><Link to="/confidentialite" className="hover:text-white">Politique de confidentialité</Link></li>
              <li><Link to="/cookies" className="hover:text-white">Politique de cookies</Link></li>
              <li><Link to="/exercer-mes-droits" className="hover:text-white">Exercer mes droits RGPD</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading text-lg mb-4">Nous contacter</div>
            <ul className="space-y-2 text-white/80 text-sm">
              <li>contact@auditionexpert.com</li>
              <li><Link to="/contact" className="hover:text-white">Formulaire de contact</Link></li>
            </ul>
            <div className="mt-6">
              <Link to="/prendre-rendez-vous">
                <Button className="bg-gold hover:bg-[#c39d33] text-primary-blue font-semibold">
                  Prendre RDV gratuit
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-white/60 text-xs flex flex-col md:flex-row justify-between gap-3">
            <div>© {new Date().getFullYear()} Audition Expert — Tous droits réservés.</div>
            <div>Partenaires officiels : Audio Pour Tous · VivaSon · Solution Audition</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
